/**
 * Minimum Order Increment (moi) quantity helpers.
 *
 * Order/allocation quantities must be exact whole-number multiples of a
 * product's moi. `moi` is stored at 4 decimals (e.g. 0.2477), so all
 * stepping/snapping here is done at FULL precision to avoid the drift that
 * `(n * moi).toFixed(2)` per step used to introduce.
 *
 * On the console we keep full (4dp) precision for both calculation AND display,
 * so suggested/compliant quantities are exact moi multiples.
 */

const MOI_EPSILON = 1e-9;

/** Nearest whole number of moi steps represented by qty (FP-tolerant). */
export function moiStepCount(qty: any, moi: any): number {
  const m = +moi;
  if (!m || m <= 0) return 0;
  return Math.max(0, Math.round((+qty || 0) / m));
}

/** Full-precision quantity for a given whole step count. */
export function moiMultiple(steps: number, moi: any): number {
  const m = +moi || 0;
  return Math.max(0, steps || 0) * m;
}

/** Smallest moi-multiple that is >= qty (snap up), full precision. */
export function snapUpToMoi(qty: any, moi: any): number {
  const m = +moi;
  if (!m || m <= 0) return +qty || 0;
  const steps = Math.ceil(((+qty || 0) / m) - MOI_EPSILON);
  return Math.max(0, steps) * m;
}

/**
 * Format a moi quantity/value for display: round to at most `maxDp` decimals
 * and drop trailing zeros (e.g. 28.98090 -> "28.9809", 5.0000 -> "5",
 * 0.2500 -> "0.25"). The console uses maxDp=4 for both quantity and moi.
 */
export function formatMoiQty(value: any, maxDp: number = 4): string {
  if (value === null || value === undefined || value === '') return '';
  const n = +value;
  if (isNaN(n)) return String(value);
  return String(parseFloat(n.toFixed(maxDp)));
}

/** Largest moi-multiple that is <= qty (snap down), full precision. */
export function snapDownToMoi(qty: any, moi: any): number {
  const m = +moi;
  if (!m || m <= 0) return +qty || 0;
  const steps = Math.floor(((+qty || 0) / m) + MOI_EPSILON);
  return Math.max(0, steps) * m;
}

/** Apply +/- one moi step to currentQty, returning the full-precision result. */
export function stepMoi(currentQty: any, moi: any, direction: 'increment' | 'decrement'): number {
  const steps = moiStepCount(currentQty, moi) + (direction === 'increment' ? 1 : -1);
  return moiMultiple(steps, moi);
}
